import React, { useMemo } from 'react';
import { formatDisplayValue } from '@customComp/Chart/_config/_utils';
import UserFilterBigNumbers from '@customComp/BigNumberChart/UserFilterBigNumbers';
import DateCompareBigNumbers from '@customComp/BigNumberChart/DateCompareBigNumbers';
import SingleBigNumber from '@customComp/BigNumberChart/SingleBigNumber';

interface IBigNumberItem {
  value: number | string;
  time_comparisons?: Array<{
    value: number | string;
    percent: number;
    date: string;
    name_en: string;
    name_cn: string;
  }>;
  control_name_en: string;
  control_name_cn: string;
  summaryEn?: string;
  summaryCn?: string;
  date?: string;
}

interface IDisplayConfig {
  columnType?: string;
  accuracy?: number;
  unit?: string;
}

interface ITransformBigNumberApiDataProps {
  bigNumbers?: IBigNumberItem[];
  displayConfig?: IDisplayConfig;
}

type TComponentProps =
  | React.ComponentProps<typeof UserFilterBigNumbers>
  | React.ComponentProps<typeof DateCompareBigNumbers>
  | React.ComponentProps<typeof SingleBigNumber>;

/**
 * 将后端返回的大数指标数据转换为前端可直接渲染的结构。
 *
 * - 当 data 长度 > 1 时：视为多对照组场景，使用 userFilter 卡片；
 * - 当 data 长度为 1 且存在 timeComparisons 时：使用 dateCompare 卡片；
 * - 其他情况（包括无对照、无数据）：使用 singleBigNumber 兜底. 实际会显示 无数据内容
 *
 * isNoData 语义：
 * - bigNumbers 为数组且 length === 0 时视为"无数据"；
 * - 其他非数组情况（undefined / null 等）统一视为"无数据"，用于上层统一兜底展示。
 */
export const transformBigNumberApiData = (
  props: ITransformBigNumberApiDataProps,
) => {
  const { bigNumbers, displayConfig } = props;
  const staticTime = bigNumbers?.[0]?.date ?? '';
  const data = bigNumbers?.map((info) => ({
    content: formatDisplayValue({
      value: info.value,
      isPercent: displayConfig?.columnType === 'percent',
      accuracy: displayConfig?.accuracy || 0,
      unit: displayConfig?.unit || 'bmk',
    }),
    timeComparisons: info?.time_comparisons?.map((item) => ({
      ...item,
      value: formatDisplayValue({
        value: item?.value,
        isPercent: displayConfig?.columnType === 'percent',
        accuracy: displayConfig?.accuracy || 0,
        unit: displayConfig?.unit || 'bmk',
      }),
    })),
    controlNameEn: info.control_name_en,
    controlNameCn: info.control_name_cn,
    summaryEn: info.summaryEn,
    summaryCn: info.summaryCn,
  }));
  let type = '';
  let componentProps: TComponentProps;
  if (data?.length > 1 || data?.[0]?.controlNameEn || data?.[0]?.controlNameCn) {
    // user filter的对照组超过1个时, 使用对应的卡片进行展示, 否则使用另一种卡片模式
    // data?.[0]?.controlNameEn || data?.[0]?.controlNameCn 用来判断 user filter对照组只有一个
    type = 'userFilter';
    componentProps = {
      data,
      staticTime,
    };
  } else if (data?.[0]?.timeComparisons?.length > 0) {
    type = 'dateCompare';
    componentProps = {
      timeComparisons: data[0].timeComparisons,
      content: data[0].content,
      staticTime,
    };
  } else {
    type = 'singleBigNumber';
    componentProps = {
      content: data?.[0]?.content ?? '',
    };
  }
  return {
    type,
    componentProps,
    staticTime,
    isNoData: Array.isArray(bigNumbers) ? bigNumbers.length === 0 : true,
  };
};

export const useTransformBigNumberApiData = (
  props: ITransformBigNumberApiDataProps,
) => {
  const { bigNumbers, displayConfig } = props;
  return useMemo(
    () => transformBigNumberApiData(props),
    [bigNumbers, displayConfig],
  );
};